import React, { useEffect, useState } from 'react';
import { Image } from '@vgn-medien-holding/vgn-fe-components';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { de } from 'date-fns/locale/de';
import { twMerge } from 'tailwind-merge';
import { FallbackCard } from '@components/atoms/FallbackCard/FallbackCard';
import { createDate, isCurrent, isCurrentOrPrevious } from '@utils/dateHelpers';
import { FairuAsset, TvChannelShowtime } from '@lib/graphql/generated';
import { IconAgain } from '@assets/icon-again';

export interface ChannelShowtimeProps {
  showtime: Partial<
    {
      image: Omit<FairuAsset, 'movies' | 'persons' | 'shows'>;
    } & Omit<TvChannelShowtime, 'image'>
  >;
  onAction: (tvChannelShowtime) => void;
}

export const ChannelShowtime = ({ showtime, onAction }: ChannelShowtimeProps) => {
  let progress = 0;
  const [currentTime, setCurrentTime] = useState(toZonedTime(new Date(), 'Europe/Vienna').getTime());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(toZonedTime(new Date(), 'Europe/Vienna').getTime());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  if (showtime) {
    const startTime = createDate(showtime?.start).getTime();
    const endTime = createDate(showtime?.stop).getTime();

    if (currentTime > endTime || currentTime < startTime) {
      progress = -1;
    } else {
      progress = Math.trunc(((currentTime - startTime) / (endTime - startTime)) * 100);
    }
  }

  const startTime = createDate(showtime.start).getTime();

  const baseStyle = twMerge(
    'w-full min-w-0 cursor-pointer border border-transparent p-4 hover:border-primary rounded-lg',
    isCurrent(showtime.start, showtime.stop, currentTime)
      ? 'bg-gray-600 p-0'
      : 'bg-gray-700 grid grid-cols-[auto,auto,1fr,auto] gap-3 items-center',
  );

  return isCurrent(showtime.start, showtime.stop, currentTime) ? (
    <div
      className={baseStyle}
      onClick={() => {
        onAction(showtime);
      }}
    >
      <div className="aspect-video p-4 pb-0">
        <div className="relative size-full overflow-hidden rounded-lg">
          {showtime?.image?.url ? (
            <Image
              src={showtime.image.url}
              copyright=""
              alt={showtime.title}
              fill
              sizes="(min-width: 768px) 300px, 100vw"
            />
          ) : (
            <FallbackCard />
          )}
        </div>
      </div>
      {progress >= 0 && (
        <div className="mt-2 h-2 w-full bg-primary/10">
          <div
            className="relative h-full bg-primary transition-size duration-100"
            style={{ width: `${progress || 0}%` }}
          >
            <div className="absolute -right-2 -top-2 grid size-5 animate-pulse place-items-center rounded-full bg-primary/60"></div>
            <div className="absolute -right-1 -top-1 grid size-3 place-items-center rounded-full bg-primary"></div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-[auto,auto,1fr,auto] items-center gap-3 p-4">
        <div className="text-sm font-bold tracking-2px text-primary">{format(startTime, 'HH:mm', { locale: de })}</div>
        <div className="inline-block h-4 w-px bg-gray-400"></div>
        <div>{showtime.title}</div>
        <div>{isCurrentOrPrevious(startTime, currentTime) && <IconAgain />}</div>
      </div>
    </div>
  ) : (
    <div
      className={baseStyle}
      onClick={() => {
        onAction(showtime);
      }}
    >
      <div className="text-sm font-bold tracking-2px text-gray-400">{format(startTime, 'HH:mm', { locale: de })}</div>
      <div className="inline-block h-4 w-px bg-gray-400"></div>
      <div>{showtime.title}</div>
      <div>{isCurrentOrPrevious(startTime, currentTime) && <IconAgain />}</div>
    </div>
  );
};
